// R-162 §10.1 / WU-3.8 — create a supervisor queue block.
//
// Picks a canonical pipeline step + a job type + a free-form reason (≤200
// chars). The block parks the (step, jobType) lane until a supervisor clears
// it. Mirrors CounterEditorModal's backdrop/field/actions structure + the
// shared queue-blocks.module.css.
import { useMemo, useState } from "react";
import type { CreateQueueBlockPayload } from "../api/client";
import { STEP_OPTIONS } from "@/lib/step-options";
import styles from "../queue-blocks.module.css";

export interface JobTypeOption {
  id: string;
  name: string;
}

const REASON_MAX = 200;

export interface CreateBlockDialogProps {
  jobTypes: JobTypeOption[];
  onClose: () => void;
  onSubmit: (payload: CreateQueueBlockPayload) => void;
}

export function CreateBlockDialog({
  jobTypes,
  onClose,
  onSubmit,
}: CreateBlockDialogProps) {
  const [stepType, setStepType] = useState<string>(STEP_OPTIONS[2]); // "key"
  const [jobTypeId, setJobTypeId] = useState<string>(jobTypes[0]?.id ?? "");
  const [reason, setReason] = useState("");

  const trimmedReason = reason.trim();
  const canSubmit = useMemo(
    () => trimmedReason.length > 0 && jobTypeId.length > 0,
    [trimmedReason, jobTypeId],
  );

  function handleSubmit() {
    onSubmit({ stepType, jobTypeId, reason: trimmedReason });
  }

  return (
    <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <h2>Block a queue</h2>

        <div className={styles.field}>
          <label htmlFor="qb-step">
            <span>Step</span>
          </label>
          <select
            id="qb-step"
            value={stepType}
            onChange={(e) => setStepType(e.target.value)}
            aria-label="step"
          >
            {STEP_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="qb-jobtype">
            <span>Job type</span>
          </label>
          <select
            id="qb-jobtype"
            value={jobTypeId}
            onChange={(e) => setJobTypeId(e.target.value)}
            aria-label="job type"
          >
            {jobTypes.length === 0 && <option value="">No job types</option>}
            {jobTypes.map((jt) => (
              <option key={jt.id} value={jt.id}>
                {jt.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="qb-reason">
            <span>Reason</span>
          </label>
          <textarea
            id="qb-reason"
            value={reason}
            maxLength={REASON_MAX}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this queue blocked?"
            aria-label="reason"
            rows={3}
          />
          <small className={styles.help}>
            Shown to operators when their feed is diverted. {reason.length} /{" "}
            {REASON_MAX}
          </small>
        </div>

        <div className={styles.actions}>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.primary}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Block queue
          </button>
        </div>
      </div>
    </div>
  );
}
